import React, { useState, useEffect, useRef, useMemo } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import type { FilterState } from "./FeedFilters";
import { fetchFacets } from "../../api/leaks";
import { Combobox } from "./Combobox";
import { useAuth } from "../../auth/AuthContext";
import { createPortal } from "react-dom";

interface MoreFiltersDrawerProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

export function MoreFiltersDrawer({ filters, onChange }: MoreFiltersDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<FilterState>(filters);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  
  const [networks, setNetworks] = useState<{ id: string; label: string; count: number }[]>([]);
  const [severities, setSeverities] = useState<{ id: string; label: string; count: number }[]>([]);
  const [sectors, setSectors] = useState<{ id: string; label: string; count: number }[]>([]);
  const [reviewStatuses, setReviewStatuses] = useState<{ id: string; label: string; count: number }[]>([]);

  useEffect(() => {
    if (isOpen) {
      setPendingFilters(filters);
      document.body.style.overflow = "hidden";
      
      // Load facets based on current filters
      Promise.all([
        fetchFacets("network", filters),
        fetchFacets("severity", filters),
        fetchFacets("sectors", filters),
        ...(user?.role !== "client" ? [fetchFacets("review_status", filters)] : [])
      ]).then(([net, sev, sec, rev]) => {
        const clean = (arr: any[]) => (arr || []).filter((x: any) => x && x.id != null && x.label != null);
        setNetworks(clean(net));
        setSeverities(clean(sev));
        setSectors(clean(sec));
        if (rev) setReviewStatuses(clean(rev));
      });
      
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen, filters, user]);
  
  const drawerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      drawerRef.current?.focus();
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const activeCount = [
    pendingFilters.network,
    pendingFilters.sector,
    pendingFilters.severity,
    pendingFilters.reviewStatus,
    pendingFilters.startDate,
    pendingFilters.endDate
  ].filter(Boolean).length + (pendingFilters.includeContent ? 1 : 0);

  const applyCount = [
    filters.network,
    filters.sector,
    filters.severity,
    filters.reviewStatus,
    filters.startDate,
    filters.endDate
  ].filter(Boolean).length + (filters.includeContent ? 1 : 0);

  const drawer = isOpen && createPortal(
    <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1300 }}>
      <div 
        className="more-filters-overlay absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={() => setIsOpen(false)}
      />
      <div 
        ref={drawerRef}
        tabIndex={-1}
        className="more-filters-panel relative w-full max-w-[420px] bg-slate-900 border-l border-slate-700 shadow-2xl flex flex-col h-full transform transition-transform duration-240 ease-out outline-none"
      >
        <div className="more-filters-header flex items-center justify-between p-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-slate-400" />
            More filters
          </h2>
          <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white p-2">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8">
          
          {/* Posted Date */}
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Posted date</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { label: "Last 24 hours", days: 1 },
                { label: "7 days", days: 7 },
                { label: "30 days", days: 30 },
                { label: "90 days", days: 90 }
              ].map(opt => (
                <button
                  key={opt.label}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${pendingFilters.days === opt.days ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                  onClick={() => setPendingFilters({ ...pendingFilters, days: pendingFilters.days === opt.days ? undefined : opt.days, startDate: undefined, endDate: undefined })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="date" 
                className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:border-indigo-500 w-full"
                value={pendingFilters.startDate || ""}
                onChange={e => setPendingFilters({ ...pendingFilters, startDate: e.target.value || undefined, days: undefined })}
              />
              <span className="text-slate-500">to</span>
              <input 
                type="date" 
                className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:border-indigo-500 w-full"
                value={pendingFilters.endDate || ""}
                onChange={e => setPendingFilters({ ...pendingFilters, endDate: e.target.value || undefined, days: undefined })}
              />
            </div>
          </div>

          {/* Network */}
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Network</h3>
            <div className="flex flex-wrap gap-2">
              {networks.map(net => (
                <button
                  key={net.id}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${pendingFilters.network === net.id ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                  onClick={() => setPendingFilters({ ...pendingFilters, network: pendingFilters.network === net.id ? undefined : net.id })}
                >
                  {net.label} <span className="text-slate-500">({net.count.toLocaleString()})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Severity */}
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Severity</h3>
            <div className="flex flex-wrap gap-2">
              {["Critical", "High", "Medium", "Low"].map(sev => {
                const isSelected = pendingFilters.severity === sev;
                let colorClass = "text-slate-400 bg-slate-800 border-slate-700";
                if (isSelected) {
                  if (sev === "Critical") colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
                  if (sev === "High") colorClass = "text-orange-400 bg-orange-500/10 border-orange-500/20";
                  if (sev === "Medium") colorClass = "text-yellow-400 bg-yellow-500/10 border-yellow-500/20";
                  if (sev === "Low") colorClass = "text-green-400 bg-green-500/10 border-green-500/20";
                }
                return (
                  <button
                    key={sev}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${colorClass} hover:border-slate-500`}
                    onClick={() => setPendingFilters({ ...pendingFilters, severity: isSelected ? undefined : sev })}
                  >
                    {sev}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sector */}
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Sector</h3>
            <Combobox 
              items={sectors} 
              selectedIds={new Set(pendingFilters.sector ? pendingFilters.sector.split(",") : [])}
              onSelect={(id) => {
                const current = new Set(pendingFilters.sector ? pendingFilters.sector.split(",") : []);
                if (current.has(id)) current.delete(id); else current.add(id);
                setPendingFilters({ ...pendingFilters, sector: Array.from(current).join(",") || undefined });
              }}
              onClearAll={() => setPendingFilters({ ...pendingFilters, sector: undefined })}
              placeholder="Select sectors..."
              searchPlaceholder="Search sectors..."
            />
          </div>
          
          {/* Review Status */}
          {user?.role !== "client" && (
            <div>
              <h3 className="text-sm font-medium text-white mb-3">Review Status</h3>
              <div className="flex flex-wrap gap-2">
                {reviewStatuses.map(rev => (
                  <button
                    key={rev.id}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${pendingFilters.reviewStatus === rev.id ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                    onClick={() => setPendingFilters({ ...pendingFilters, reviewStatus: pendingFilters.reviewStatus === rev.id ? undefined : rev.id })}
                  >
                    <span className="capitalize">{(rev.label || rev.id || '').replace('_', ' ')}</span> <span className="text-slate-500">({(rev.count ?? 0).toLocaleString()})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Scope */}
          {user?.role !== "client" && (
            <div>
              <h3 className="text-sm font-medium text-white mb-3 flex items-center justify-between">
                Search scope
                <span className="text-xs text-slate-500 font-normal">Searching post content is slower</span>
              </h3>
              <div className="flex bg-slate-800 p-1 rounded-lg border border-slate-700">
                <button
                  className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${!pendingFilters.includeContent ? "bg-slate-700 text-white shadow" : "text-slate-400 hover:text-white"}`}
                  onClick={() => setPendingFilters({ ...pendingFilters, includeContent: false })}
                >
                  Titles & details
                </button>
                <button
                  className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${pendingFilters.includeContent ? "bg-slate-700 text-white shadow" : "text-slate-400 hover:text-white"}`}
                  onClick={() => setPendingFilters({ ...pendingFilters, includeContent: true })}
                >
                  Include post content
                </button>
              </div>
            </div>
          )}

        </div>
        
        <div className="more-filters-footer p-4 border-t border-slate-800 sticky bottom-0 bg-slate-900 z-10 flex items-center justify-between">
          <button 
            className="text-sm text-slate-400 hover:text-white"
            onClick={() => setPendingFilters({
              ...pendingFilters,
              network: undefined,
              sector: undefined,
              severity: undefined,
              reviewStatus: undefined,
              days: undefined,
              startDate: undefined,
              endDate: undefined,
              includeContent: false
            })}
          >
            Reset
          </button>
          <button 
            className="bg-indigo-500 hover:bg-indigo-400 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            onClick={() => {
              onChange(pendingFilters);
              setIsOpen(false);
            }}
          >
            Show results {activeCount > 0 && `(${activeCount} applied)`}
          </button>
        </div>
      </div>
    </div>
  , document.body);

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 h-9 px-3 text-sm rounded-lg transition-colors border ${applyCount > 0 ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20" : "bg-transparent border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"}`}
      >
        <SlidersHorizontal size={14} />
        More filters
        {applyCount > 0 && (
          <span className="bg-indigo-500/20 text-indigo-400 text-xs px-1.5 py-0.5 rounded-full ml-1">
            {applyCount}
          </span>
        )}
      </button>
      {drawer}
    </>
  );
}
